import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useWindowWidth } from '@react-hook/window-size';
import { useDialog } from 'utils/dialog';
import snackbar from 'utils/snackbar';

import CustomLoader from '../../components/CustomLoader/CustomLoader';
import { ReduxState } from '../../redux/types';
import Company, { EmptyCompany } from '../../types/Company';
import api from '../../utils/api';
import Card4 from './components/Card4/Card4';
import Card5 from './components/Card5/Card5';
import Card6 from './components/Card6/Card6';
import Card7 from './components/Card7/Card7';
import Card8 from './components/Card8/Card8';
import Card9 from './components/Card9/Card9';
import SelfAssessmentDialog from './components/Card9/compnents/SelfAssessmentDialog/SelfAssessmentDialog';
import Co2Card from './components/Co2Card/Co2Card';
import InfoCard from './components/InfoCard/InfoCard';
import StateCard from './components/StateCard/StateCard';
import { Content, FlexContainer } from './styled';

const NewDashboardPage = () => {
    const userInfo = useSelector((s: ReduxState) => s.user);
    const { companyId } = userInfo;

    const { t } = useTranslation();
    const [openDialog, closeDialog] = useDialog();

    const [isLoading, setIsLoading] = useState(true);
    const [companyData, setCompanyData] = useState<Company>(EmptyCompany);

    const [isSelfAssessmentDialogOpen, setIsSelfAssessmentDialogOpen] = useState(false);

    const windowWidth = useWindowWidth();

    const stateInfo = {
        prospect: {
            showAutoAssessmentDialog: true,
            next: 'audit',
            text: t('pages.dashboard.cards.state.next.prospect.button'),
            message: t('pages.dashboard.cards.state.next.prospect.message')
        },
        audit: {
            next: 'want_cancel_audit',
            text: t('pages.dashboard.cards.state.next.audit.button'),
            message: t('pages.dashboard.cards.state.next.audit.message')
        },
        want_cancel_audit: {
            next: 'audit',
            text: t('pages.dashboard.cards.state.next.want_cancel_audit.button'),
            message: null
        },
        audit_accepted: {
            next: null,
            text: '',
            message: null
        },
        certified: {
            next: null,
            text: '',
            message: null
        },
        expired: {
            next: null,
            text: '',
            message: null
        },
        corrective_actions_management: {
            next: null,
            text: '',
            message: null
        },
        audit_completed: {
            next: null,
            text: '',
            message: null
        },
    };

    const getCompanyData = useCallback(() => {
        api.request('/companies/' + companyId).then(res => {
            setCompanyData(res);
            setIsLoading(false);
        });
    }, [companyId]);

    useEffect(() => {
        getCompanyData();
    }, [getCompanyData]);

    const { state } = companyData;
    const currentStateInfo = stateInfo[state as ('prospect' | 'audit' | 'want_cancel_audit')];
    const { next, message } = currentStateInfo;

    const updateState = useCallback(() => {
        api.request('/companies/' + companyId + '/state', 'PUT', { state: next }, true, false, false).then(() => {
            getCompanyData();

            snackbar.success(t('dialogs.company_details.state_changed'));
        }).catch(res => {
            if (res.error) {
                setTimeout(() => {
                    openDialog({
                        title: next === 'audit' ? t('dialogs.update_company_state.cannot_audit') : t('dialogs.update_company_state.error_title'),
                        message: res.error,
                        actions: [
                            {
                                text: t('dialogs.generic.close'),
                                color: 'primary',
                                action: closeDialog
                            },
                        ]
                    });
                }, 250);
            }
        });
    }, [closeDialog, openDialog, t, companyId, next, getCompanyData]);

    const showUpdateStateDialog = useCallback(() => {
        if (message === null) {
            updateState();
            return;
        }

        openDialog({
            title: t('dialogs.update_company_state.title'),
            message: message ?? '',
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        updateState();
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, message, openDialog, t, updateState]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <div style={{ fontSize: windowWidth < 770 ? '1.2em' : '2em', fontWeight: 400, color: '#164361', width: windowWidth > 800 ? '950px' : '100%', margin: '16px auto 24px', textAlign: 'center' }} >
                {t('pages.dashboard.description')}
            </div>

            <div style={{ margin: '0 8px', backgroundColor: '#6EA8DA', color: '#ffffff', padding: '8px', textAlign: 'center', fontSize: '1.2em', fontWeight: 1000 }}>
                {t('pages.dashboard.situation')}
            </div>

            <Content>
                <FlexContainer>
                    <InfoCard company={companyData} />
                    <StateCard company={companyData} />
                    <Co2Card company={companyData} />

                    <Card4 company={companyData} updateData={getCompanyData} />
                    <Card5 company={companyData} updateData={getCompanyData} />
                    <Card6 company={companyData} updateData={getCompanyData} />

                    <Card7 company={companyData} updateData={getCompanyData} />
                    <Card9 setIsSelfAssessmentDialogOpen={setIsSelfAssessmentDialogOpen} />
                    <Card8 currentStateInfo={currentStateInfo} setIsSelfAssessmentDialogOpen={setIsSelfAssessmentDialogOpen} showUpdateStateDialog={showUpdateStateDialog} />
                </FlexContainer>
            </Content>

            <SelfAssessmentDialog
                open={isSelfAssessmentDialogOpen}
                onClose={() => setIsSelfAssessmentDialogOpen(false)}
                assessmentId={companyData?.chartData?.currentAssessmentId ?? ''}
                showUpdateStateDialog={showUpdateStateDialog}
                showAuditButton={next === 'audit'}
            />
        </>
    );
};

export default NewDashboardPage;
